import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import AssignmentModal from "../../../../Components/Common/AssignmentModal";
import PointForm from "./PointForm";
import {
  getEmissionPoints,
  updateEmissionPoint,
  addEmissionPoint,
  deleteEmissionPoint,
} from "../../../../slices/companies/thunk";
import { assignPointThunk, hydrateAssignmentsFromToken } from "../../../../slices/assignments";
import AssignmentAlert from "./AssignmentAlert";

const PointsManager = ({ establishment, onBack, executeWithPasswordValidation }: any) => {
  const dispatch: any = useDispatch();
  const { emissionPointList, isSaving, isLoading } = useSelector((state: any) => ({
    emissionPointList: state.Companies.emissionPointList,
    isSaving: state.Companies.isSaving,
    isLoading: state.Companies.isLoading,
  }));
  const { currentPoint } = useSelector((state: any) => state.Assignment);

  const [isCreating, setIsCreating] = useState(false);
  const [editingPoint, setEditingPoint] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pointToDelete, setPointToDelete] = useState<string | null>(null);
  const [assignmentModal, setAssignmentModal] = useState<{ show: boolean; type: string; point: any }>({ show: false, type: '', point: null });
  const [isConfirming, setIsConfirming] = useState(false);
  
  useEffect(() => {
      dispatch(getEmissionPoints(establishment.id));
  }, [dispatch, establishment.id]);

  // Inicializar asignaciones desde token JWT
  useEffect(() => {
      dispatch(hydrateAssignmentsFromToken() as any);
  }, [dispatch]);

  const points = useMemo(() => {
      if (!emissionPointList) return [];
      return emissionPointList;
  }, [emissionPointList]);

  const handleSave = (values: any) => {
      const payload = { ...values, establishmentId: establishment.id };
      
      const savePoint = () => {
          if (editingPoint) {
              dispatch(updateEmissionPoint({ ...payload, id: editingPoint.id })).then((result: any) => {
                 if (!result.error) {
                     dispatch(getEmissionPoints(establishment.id));
                     setEditingPoint(null);
                 }
              });
          } else {
              dispatch(addEmissionPoint(payload)).then((result: any) => {
                 if (!result.error) {
                     dispatch(getEmissionPoints(establishment.id));
                     setIsCreating(false);
                 }
              });
          }
      };

      executeWithPasswordValidation(savePoint);
  };

  const onClickDelete = (id: string) => {
      setPointToDelete(id);
      setDeleteModal(true);
  };

  const confirmDelete = () => {
      if (pointToDelete) {
        dispatch(deleteEmissionPoint(pointToDelete)).then((result: any) => {
            if (!result.error) {
                dispatch(getEmissionPoints(establishment.id));
                setDeleteModal(false);
                setPointToDelete(null);
            }
        });
      }
  };

  const handleAssignPoint = async (payload: any) => {
    const point = emissionPointList?.find((p: any) => p.id === payload.pointId);
    if (point) {
        setAssignmentModal({ 
          show: true, 
          type: 'point',
          point: point
        });
    }
  };

  const confirmAssignment = async () => {
    if (assignmentModal.point) {
        try {
            const assignPointAction = async () => {
                await dispatch(assignPointThunk({ 
                    establishmentId: establishment.id, 
                    emissionPointId: assignmentModal.point.id 
                }) as any);
                setAssignmentModal({ show: false, type: '', point: null });
            };

            executeWithPasswordValidation(async () => {
                setIsConfirming(true);
                try {
                    await assignPointAction();
                } catch (error) {
                    console.error("Error en asignación:", error);
                } finally {
                    setIsConfirming(false);
                }
            });
        } catch (error) {
            console.error("Error en confirmAssignment:", error);
            setIsConfirming(false);
        }
    }
  };

  if (isCreating || editingPoint) {
      return <PointForm point={editingPoint} isCreating={isCreating} onSubmit={handleSave} onCancel={() => { setIsCreating(false); setEditingPoint(null); }} isSaving={isSaving} />;
  }

  return (
      <div className="fade-in">
          <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2 rounded">
              <Button color="link" className="p-0 text-decoration-none text-dark" onClick={onBack}><i className="ri-arrow-left-line align-bottom me-1"></i> Volver</Button>
              <h6 className="mb-0 fw-bold text-primary">{establishment.code} - {establishment.name}</h6>
              <Button color="primary" size="sm" onClick={() => setIsCreating(true)} disabled={isLoading}><i className="ri-add-line align-bottom"></i> {isLoading ? 'Cargando...' : 'Nuevo Punto'}</Button>
          </div>
          <AssignmentAlert
              currentEstablishment={establishment}
              currentPoint={currentPoint}
              establishments={[establishment]}
              onAddEstablishment={() => {}}
              onSelectEstForPoints={() => {}}
          />
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" className="me-2" />
              <span className="text-muted">Cargando puntos de emisión...</span>
            </div>
          ) : points.length > 0 ? (
            <div className="vstack gap-2">
              {points.map((pt: any) => (
                <Card key={pt.id} className="mb-0 border shadow-none">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm flex-shrink-0 me-3">
                          <div className="avatar-title bg-light text-primary rounded-circle fs-4">
                            {pt.code}
                          </div>
                        </div>
                        <div>
                          <h5 className="fs-14 mb-2">Punto de Emisión {pt.code}</h5>
                          {String(currentPoint ?? '') === String(pt.id ?? '') && (
                            <Badge color="success" className="mb-2">
                              <i className="bx bx-check-circle me-1"></i>
                              Asignado
                            </Badge>
                          )}
                            <div className="d-flex gap-3 flex-wrap">
                                <Badge color="light" className="text-muted border p-2 fs-13" title="Factura">
                                    Sec. Última Factura: <span className="text-dark fw-bold ms-1">{pt.currentInvoiceSeq}</span>
                                </Badge>

                                <Badge color="light" className="text-muted border p-2 fs-13" title="Retención">
                                    Sec. Última Retención: <span className="text-dark fw-bold ms-1">{pt.currentRetentionSeq}</span>
                                </Badge>

                                <Badge color="light" className="text-muted border p-2 fs-13" title="Guía">
                                    Sec. Última Guía: <span className="text-dark fw-bold ms-1">{pt.currentWaybillSeq}</span>
                                </Badge>
                            </div>
                        </div>
                      </div>
                        <div className="d-flex gap-2">
                            {String(currentPoint ?? '') !== String(pt.id ?? '') && (
                                <Button
                                    color="primary"
                                    size="sm"
                                    title="Asignarme a este punto de emisión"
                                    onClick={() => handleAssignPoint({ establishmentId: establishment.id, pointId: pt.id })}
                                    disabled={isConfirming || isLoading}
                                >
                                    {isConfirming && assignmentModal.point?.id === pt.id ? (
                                        <><Spinner size="sm" className="me-1" /> Asignando...</>
                                    ) : (
                                        <i className="bx bx-user-check"></i>
                                    )}
                                </Button>
                            )}
                            <Button
                                color="warning"
                                size="sm"
                                title="Editar"
                                onClick={() => setEditingPoint(pt)}
                                disabled={isLoading}
                            >
                                <i className="ri-pencil-line"></i>
                            </Button>

                            <Button
                                color="danger"
                                size="sm"
                                title="Eliminar"
                                onClick={() => onClickDelete(pt.id)}
                                disabled={isLoading}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </Button>
                        </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="ri-map-pin-line display-5 mb-2"></i>
              <p>No hay puntos de emisión registrados.</p>
            </div>
          )}
          <DeleteModal show={deleteModal} onDeleteClick={confirmDelete} onCloseClick={() => setDeleteModal(false)} />
          
          <AssignmentModal
            show={assignmentModal.show}
            setOpen={(open) => !open && setAssignmentModal({ show: false, type: '', point: null })}
            title="Confirmar Asignación"
            message={`¿Estás seguro de que deseas asignarte al punto de emisión ${assignmentModal.point ? assignmentModal.point.code : ''} del establecimiento ${establishment.code}?`}
            onConfirm={confirmAssignment}
            isConfirming={isConfirming}
            confirmText="Asignarme"
            cancelText="Cancelar"
          />
      </div>
  );
};

export default PointsManager;
